import { SeasonTeamRow } from "@/features/credentials/components/season-team-row";
import { addSelectedIds, removeSelectedIds } from "@/features/credentials/lib/toggle-selected-ids";
import type { SeasonTeamGroup } from "@/features/credentials/models/season-team-tree";
import { cn } from "@/lib/utils";
import { Badge, Checkbox } from "@cmde/shadcn";
import { ChevronRightIcon, Loader2Icon } from "lucide-react";
import { useTranslation } from "react-i18next";

export function SeasonTeamNode({
  season,
  selectedIds,
  expanded,
  onExpandedChange,
  onToggleAll,
  onChange,
}: {
  season: SeasonTeamGroup;
  selectedIds: string[];
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onToggleAll: (checked: boolean) => void;
  onChange: (nextIds: string[]) => void;
}) {
  const { t } = useTranslation();
  const selectedIdSet = new Set(selectedIds);
  const teamIds = season.teams.map((team) => team.entityId);
  const selectedCount = season.teams.filter((team) => selectedIdSet.has(team.entityId)).length;
  const hasTeams = teamIds.length > 0;
  const allSelected = hasTeams && selectedCount === teamIds.length;
  const seasonName = season.name || t("credentials.create.fields.unnamed_season");
  const selectAllId = `season-select-all-${season.seasonId}`;

  return (
    <div className="odd:bg-muted/10 dark:odd:bg-white/3 group">
      <div className="flex items-center gap-2 py-2 pr-4 hover:bg-muted/40 group-hover:bg-muted/40">
        <button
          type="button"
          aria-label={
            expanded
              ? t("credentials.create.fields.collapse_season")
              : t("credentials.create.fields.expand_season")
          }
          onClick={() => onExpandedChange(!expanded)}
          className="flex size-8 shrink-0 items-center justify-center"
        >
          <ChevronRightIcon
            className={cn(
              "size-4 text-muted-foreground transition-transform",
              expanded && "rotate-90",
            )}
          />
        </button>

        <Checkbox
          variant="simple"
          id={selectAllId}
          checked={allSelected}
          disabled={Boolean(season.isLoading)}
          aria-label={t("credentials.create.fields.select_all_teams")}
          onCheckedChange={onToggleAll}
        />

        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm font-medium"
          onClick={() => onExpandedChange(!expanded)}
        >
          <span className="truncate">{seasonName}</span>
          {selectedCount > 0 ? (
            <Badge variant="secondary" className="text-xs">
              {t("credentials.create.fields.teams_selected", { count: selectedCount })}
            </Badge>
          ) : null}
        </button>
      </div>

      {expanded ? (
        <div className="divide-y divide-border/50">
          {season.isLoading && season.teams.length === 0 ? (
            <div className="text-muted-foreground flex items-center gap-2 py-2 pr-4 pl-10 text-sm">
              <Loader2Icon className="size-4 animate-spin" />
              {t("credentials.create.fields.loading_teams")}
            </div>
          ) : season.teams.length === 0 ? (
            <p className="text-muted-foreground py-2 pr-4 pl-10 text-sm">
              {t("credentials.create.fields.no_teams_in_season")}
            </p>
          ) : (
            season.teams.map((team) => (
              <SeasonTeamRow
                key={team.entityId}
                team={team}
                checkboxId={`season-team-${season.seasonId}-${team.entityId}`}
                checked={selectedIdSet.has(team.entityId)}
                onCheckedChange={(checked) =>
                  onChange(
                    checked
                      ? addSelectedIds(selectedIds, [team.entityId])
                      : removeSelectedIds(selectedIds, [team.entityId]),
                  )
                }
              />
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
